import React from 'react';
import { FiltersMap, KeyedItem } from '@wix/bex-core';
import { GridCardItemProps } from './GridCardItem';
import { GridState, ItemActionsState } from '../../state';
import { ActionsCellProps } from '../ActionsCell';
import {
  CardGalleryItemProps,
  IconButton,
  PopoverMenu,
  TableActionCellSecondaryAction,
  TableActionCellSecondaryDivider,
} from '@wix/design-system';
import { More } from '@wix/wix-ui-icons-common';
import {
  createPrimaryActionCellPropsWithBI,
  createSecondaryActionCellPropWithBi,
  reportOnPopoverMenuShow,
} from '../CollectionItemActions/CollectionItemActionsHelper';

interface ActionsCell<T, F extends FiltersMap> {
  actionsCell: GridCardItemProps<T, F>['actionsCell'];
  item: T;
  index: number;
  gridState: GridState<T, F>;
  keyedItem: KeyedItem<T>;
  itemActionsState: ItemActionsState<T, F>;
}

export function evaluateActionsCell<T, F extends FiltersMap>({
  actionsCell,
  item,
  index,
  gridState,
  keyedItem,
  itemActionsState,
}: ActionsCell<T, F>): ActionsCellProps {
  if (!actionsCell) {
    return {};
  }

  if (typeof actionsCell === 'function') {
    return actionsCell(item, index, {
      openConfirmDeleteModal: itemActionsState.openConfirmDeleteModal,
      openConfirmModal: itemActionsState.openConfirmModal,
      collection: gridState.collection,
      keyedItem,
    });
  }

  return actionsCell;
}

export function toGridItemActions<T, F extends FiltersMap>(
  actionsCell: ActionsCell<T, F> & {
    itemActionsState: ItemActionsState<T, F>;
    imagePlacement: string;
  },
) {
  const actions = evaluateActionsCell(actionsCell);

  let settingsMenu: CardGalleryItemProps['settingsMenu'] = null;
  let primaryAction: CardGalleryItemProps['primaryActionProps'] | null = null;

  const extensionMenuItems =
    actionsCell.gridState.toolbar.extensionMenuItems?.useExtensionMenuItems?.({
      containerId: actions.containerId,
    });

  const secondaryActionCellPropsWithBI = createSecondaryActionCellPropWithBi({
    toolbarBIReporter: actionsCell.gridState.toolbar.toolbarBIReporter,
    actionCellProps: actions,
    keyedItem: actionsCell.keyedItem,
    extensionMenuItems,
  });

  const primaryActionCellPropsWithBI = createPrimaryActionCellPropsWithBI(
    actionsCell.gridState.toolbar.toolbarBIReporter,
    actions,
    actionsCell.keyedItem,
  );

  if (primaryActionCellPropsWithBI) {
    primaryAction = {
      label: primaryActionCellPropsWithBI.text,
      onClick: primaryActionCellPropsWithBI.onClick,
      disabled: primaryActionCellPropsWithBI.disabled,
      disabledMessage: primaryActionCellPropsWithBI.disabledMessage,
    };
  }

  if (
    secondaryActionCellPropsWithBI &&
    secondaryActionCellPropsWithBI.length > 0
  ) {
    settingsMenu = (
      <PopoverMenu
        dataHook="popover"
        triggerElement={({ toggle, close }) => (
          <IconButton
            dataHook="popover-trigger"
            onClick={(e) => {
              e.stopPropagation();
              toggle();
              reportOnPopoverMenuShow(
                actionsCell.gridState.toolbar.toolbarBIReporter,
                actions,
                actionsCell.keyedItem,
              );
            }}
            onPointerDown={
              actionsCell.gridState._dnd
                ? (e) => e.stopPropagation()
                : undefined
            }
            onMouseLeave={close}
            skin={actionsCell.imagePlacement === 'top' ? 'light' : 'standard'}
            size="small"
            priority="secondary"
          >
            <More />
          </IconButton>
        )}
        children={secondaryActionCellPropsWithBI.map((action, index) => {
          if ((action as TableActionCellSecondaryDivider).divider) {
            return <PopoverMenu.Divider key={index} />;
          }

          const { text, icon, onClick } =
            action as TableActionCellSecondaryAction;

          return (
            <PopoverMenu.MenuItem
              key={index}
              text={text}
              prefixIcon={icon}
              onClick={onClick}
            />
          );
        })}
      />
    );
  }

  return {
    primaryAction,
    settingsMenu,
  };
}
